// ABOUTME: Apollo-backed hook exposing the notification list, unread count and read mutations.
// ABOUTME: Defaults to unread-only; pass onlyUnread=false ("Show all") to include read ones.

import { useCallback } from 'react';

import {
    useMarkAllNotificationsReadMutation,
    useMarkNotificationReadMutation,
    useNotificationsQuery,
    useUnreadNotificationCountQuery,
} from '../graphql/notifications.generated';

export function useNotifications({ onlyUnread = true }: { onlyUnread?: boolean } = {}) {
    const listQuery = useNotificationsQuery({
        fetchPolicy: 'cache-and-network',
        // Without this, Apollo keeps loading=false during refetches (panel open, "Show all"
        // toggle), so the UI would never show a loading state after the first fetch.
        notifyOnNetworkStatusChange: true,
        variables: { onlyUnread },
    });
    const countQuery = useUnreadNotificationCountQuery({ fetchPolicy: 'cache-and-network' });
    const [markNotificationRead] = useMarkNotificationReadMutation();
    const [markAllNotificationsRead] = useMarkAllNotificationsReadMutation();

    // Depend on the stable Apollo refetch functions, NOT on the query result objects: those get
    // a new identity on every render, which would make this callback (and any effect keyed on
    // it) re-fire after each loading-state change — an infinite refetch loop.
    const refetchList = listQuery.refetch;
    const refetchCount = countQuery.refetch;
    const refetch = useCallback(() => {
        refetchList();
        refetchCount();
    }, [refetchList, refetchCount]);

    const markAsRead = useCallback(
        async (id: string) => {
            await markNotificationRead({ variables: { id } });
            refetch();
        },
        [markNotificationRead, refetch],
    );

    const markAllAsRead = useCallback(async () => {
        await markAllNotificationsRead();
        refetch();
    }, [markAllNotificationsRead, refetch]);

    return {
        notifications: listQuery.data?.notifications ?? [],
        unreadCount: countQuery.data?.unreadNotificationCount ?? 0,
        loading: listQuery.loading || countQuery.loading,
        refetch,
        markAsRead,
        markAllAsRead,
    };
}
